import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import * as ErrorMessages from 'ApplicationEntry/components/WorkFlow/ErrorMessages';
import FixedCodeRiderInformation from 'ApplicationEntry/components/WorkFlow/ProductsRiders/FixedCodeRiderInformation';
import 'ApplicationEntry/components/WorkFlow/ProductsRiders/Ordinary/Accident.sass';
import 'ApplicationEntry/components/WorkFlow/ProductsRiders/ProductRiders.sass';
import {validateTICAgent} from 'ApplicationEntry/components/WorkFlow/ValidateApplication';
import * as AActions from 'ApplicationEntry/ducks/ProductsRiders/accident';
import {AgentList} from 'ApplicationEntry/models/AgentList';
import {ApplicationModeType} from 'ApplicationEntry/models/ApplicationModeType';
import {isValidPolicyNumberPaperElectronic} from 'ApplicationEntry/models/EnteredPolicyNumber';
import * as A from 'ApplicationEntry/models/ProductsRiders/Accident/OrdinaryAccident';
import {StringUnion} from 'Core/models/EmptyString';
import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';

type AccidentProps = {
  accident: A.OrdinaryAccident;
  agentList: AgentList;
  situs: StringUnion;
  mode: ApplicationModeType;
};

const Accident = (props: AccidentProps) => {
  const dispatch = useDispatch();
  const {
    planType,
    productType,
    overrideBillForm,
    calculatedPremium,
    riderInformation,
    cwa,
    policyNumber,
    replacement,
    replacementEnabled,
    section125,
    baseUnits,
    apdir1,
    apdir1Enabled,
    selectCgi,
    selectCgiEnabled,
    monthlySalary,
  } = props.accident;
  const [planTypeBlurred, setBlurPlanType] = useState(false);
  const [policyNumberBlurred, setPolicyNumberBlurred] = useState(false);
  const showPlanTypeError = planTypeBlurred && planType.key === 'Empty';
  const [planTypeLabel, setPlanTypeLabel] = useState<HTMLLabelElement | null>();
  const [typeBlurred, setBlurType] = useState(false);
  const [enableAPDIRCheckbox, setenableAPDIRCheckbox] = useState(false);
  const [typeLabel, setTypeLabel] = useState<HTMLLabelElement | null>();
  const showTypeError = typeBlurred && productType.key === 'Empty';

  const showPolicyNumberError = policyNumberBlurred && !isValidPolicyNumberPaperElectronic(policyNumber) && replacement;

  useEffect(() => {
    if (props.mode === ApplicationModeType.Quality) {
      dispatch(AActions.changeState(props.situs.value));
    }
    if (
      props.situs.value === 'CA' &&
      (props.accident.productType.value === 'AP2' || props.accident.productType.value === 'AP3')
    ) {
      const riderAmountArray = props.accident.riderInformation.amounts.toArray();
      if (riderAmountArray[0] !== undefined) {
        setenableAPDIRCheckbox(true);
        dispatch(AActions.changeApdir1(true));
      } else {
        setenableAPDIRCheckbox(false);
        dispatch(AActions.changeApdir1(false));
        dispatch(AActions.changeSelectCgi(false, productType.value));
      }
    } else {
      setenableAPDIRCheckbox(false);
      dispatch(AActions.changeApdir1(false));
      dispatch(AActions.changeSelectCgi(false, productType.value));
    }
  }, [props.situs.value]);

  return (
    <>
      <Grid className="product-riders-section__policy-type" id="accident-section">
        <form noValidate autoComplete="off" className="product-riders-section__policy-type__form">
          <div className="product-riders-section__policy-type__form__row">
            <Grid container spacing={4}>
              <Grid item xs={12} md={3}>
                <FormControl
                  error={showTypeError}
                  variant="outlined"
                  className="product-riders-section__policy-type__form__row--field"
                >
                  <InputLabel ref={setTypeLabel} id="accident-product-type-label">
                    Product Type
                  </InputLabel>
                  <Select
                    labelId="accident-label--product-type"
                    labelWidth={typeLabel?.clientWidth}
                    id="accident-product-type"
                    value={productType.key === 'Empty' ? '' : productType.value}
                    onBlur={() => setBlurType(true)}
                    onChange={event => dispatch(AActions.changeProductType(event.target.value as string))}
                    displayEmpty
                  >
                    {A.typeList.map(product => (
                      <MenuItem value={product} key={product}>
                        {product}
                      </MenuItem>
                    ))}
                  </Select>
                  {showTypeError && <FormHelperText>{ErrorMessages.REQUIRED_MESSAGE}</FormHelperText>}
                </FormControl>
              </Grid>
            </Grid>

            {productType.key === 'NonEmpty' && productType.value === 'AP6' ? (
              <>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={3}>
                    <FormControl
                      error={showPlanTypeError}
                      variant="outlined"
                      className="product-riders-section__policy-type__form__row--field"
                    >
                      <InputLabel ref={setPlanTypeLabel} id="accident-plantype-label">
                        Plan Type
                      </InputLabel>
                      <Select
                        labelId="accident-label-plantype"
                        labelWidth={planTypeLabel?.clientWidth}
                        id="critical-illness-plantype"
                        value={planType.key === 'Empty' ? '' : planType.value}
                        onBlur={() => setBlurPlanType(true)}
                        onChange={event => {
                          dispatch(AActions.changePlanType(event.target.value as string));
                        }}
                      >
                        <MenuItem value="IND">Individual</MenuItem>
                        <MenuItem value="FAMILY">Family</MenuItem>
                        <MenuItem value="INDSPS">Ind+Sp</MenuItem>
                        <MenuItem value="INDCH">Ind+Ch</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="checkedB"
                          color="secondary"
                          id="accident-replacement"
                          disabled={!replacementEnabled}
                          checked={replacement}
                          onChange={() => dispatch(AActions.changeReplacement(!replacement))}
                        />
                      }
                      label={<Typography className="case-details-section__checkbox-label">Replacement</Typography>}
                      className="product-riders-section__policy-type__form__row--field"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      id="accident-policy-number"
                      label="Policy Number"
                      variant="outlined"
                      placeholder={replacementEnabled ? 'Enter Policy Number' : ''}
                      margin="normal"
                      fullWidth
                      error={showPolicyNumberError}
                      helperText={showPolicyNumberError ? ErrorMessages.INVALID_POLICY_NUMBER : ''}
                      className="product-riders-section__policy-type__form__row--field"
                      value={policyNumber.key !== 'ValidPolicyNumber' || !replacement ? '' : policyNumber.value.value}
                      onBlur={() => setPolicyNumberBlurred(true)}
                      onChange={event => dispatch(AActions.changePolicyNumber(event.target.value as string))}
                      disabled={!replacement}
                      inputProps={{
                        maxLength: 10,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="checkedB"
                          color="secondary"
                          id="accident-override-bill-form"
                          checked={overrideBillForm === null ? false : overrideBillForm}
                          onChange={() => dispatch(AActions.changeOverrideBillForm(!overrideBillForm))}
                        />
                      }
                      label={<Typography>Override Bill Form To Direct</Typography>}
                      className="product-riders-section__policy-type__form__row--field"
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="checkedB"
                          color="secondary"
                          id="accident-section125"
                          checked={section125}
                          onChange={() => dispatch(AActions.changeSection125(!section125))}
                        />
                      }
                      label={<Typography>Section 125</Typography>}
                      className="product-riders-section__policy-type__form__row--field"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="checkedB"
                          color="secondary"
                          id="accident-select-cgi"
                          checked={selectCgi === null ? false : selectCgi}
                          onChange={() => dispatch(AActions.changeSelectCgi(!selectCgi, productType.value))}
                          disabled={!selectCgiEnabled}
                        />
                      }
                      label={<Typography>Select CGI</Typography>}
                      className="product-riders-section__policy-type__form__row--field"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="checkedB"
                          color="secondary"
                          id="accident-apdir1"
                          checked={apdir1 === null ? false : enableAPDIRCheckbox || apdir1}
                          value={apdir1 || enableAPDIRCheckbox}
                          onChange={() => dispatch(AActions.changeApdir1(!apdir1))}
                          disabled={!apdir1Enabled}
                        />
                      }
                      label={<Typography>APDIR1</Typography>}
                      className="product-riders-section__policy-type__form__row--field"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      id="accident-premium"
                      label="Premium"
                      variant="outlined"
                      placeholder="Enter Premium"
                      margin="normal"
                      fullWidth
                      className="product-riders-section__policy-type__form__row--field"
                      value={calculatedPremium.value}
                      required={section125}
                      onChange={event => {
                        dispatch(AActions.changePremium(event.target.value as string));
                        dispatch(AActions.changeCalculatedPremium(event.target.value as string));
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      id="accident-cwa"
                      label="CWA"
                      variant="outlined"
                      placeholder="Enter CWA"
                      margin="normal"
                      fullWidth
                      className="product-riders-section__policy-type__form__row--field"
                      value={cwa.value}
                      onChange={event => dispatch(AActions.changeCWA(event.target.value as string))}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      id="accident-monthly-salary"
                      label="Monthly Salary"
                      variant="outlined"
                      placeholder="Enter Monthly Salary"
                      margin="normal"
                      fullWidth
                      className="product-riders-section__policy-type__form__row--field"
                      value={monthlySalary.value}
                      onChange={event => dispatch(AActions.changeMonthlySalary(event.target.value as string))}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl
                      error={showTypeError}
                      variant="outlined"
                      className="product-riders-section__policy-type__form__row--field"
                    >
                      <InputLabel ref={setTypeLabel} id="accident-type-label">
                        Base Units
                      </InputLabel>
                      <Select
                        labelId="accident-label-type"
                        labelWidth={typeLabel?.clientWidth}
                        id="accident-type"
                        value={baseUnits.key === 'Empty' ? '' : baseUnits.value}
                        onBlur={() => setBlurType(true)}
                        onChange={event => dispatch(AActions.changeBaseUnits(event.target.value as string))}
                        displayEmpty
                      >
                        {A.baseUnits.map(baseUnit => (
                          <MenuItem value={baseUnit} key={baseUnit}>
                            {baseUnit}
                          </MenuItem>
                        ))}
                      </Select>
                      {showTypeError && <FormHelperText>{ErrorMessages.REQUIRED_MESSAGE}</FormHelperText>}
                    </FormControl>
                  </Grid>
                </Grid>

                <div>
                  <FixedCodeRiderInformation
                    riderInformation={riderInformation}
                    changeRiderInformationAction={AActions.changeRiderInformation}
                    isDisabled={(code: string) => {
                      switch (productType.value) {
                        case 'AP6':
                          switch (code) {
                            case 'APD':
                            case 'APDI':
                            case 'APH':
                            case 'EXT':
                              return true;
                            default:
                              return false;
                          }
                        default:
                          return true;
                      }
                    }}
                    identifier={'ordinary-accident'}
                  />
                </div>
              </>
            ) : null}

            {productType.key === 'NonEmpty' &&
            (productType.value === 'AP2' ||
              productType.value === 'AP3' ||
              productType.value === 'AP2C' ||
              productType.value === 'AP3C' ||
              productType.value === 'SAP2' ||
              productType.value === 'SAP3') ? (
              <>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={3}>
                    <FormControl
                      error={showPlanTypeError}
                      variant="outlined"
                      className="product-riders-section__policy-type__form__row--field"
                    >
                      <InputLabel ref={setPlanTypeLabel} id="accident-plantype-label">
                        Plan Type
                      </InputLabel>
                      <Select
                        labelId="accident-label-plantype"
                        labelWidth={planTypeLabel?.clientWidth}
                        id="critical-illness-plantype"
                        value={planType.key === 'Empty' ? '' : planType.value}
                        onBlur={() => setBlurPlanType(true)}
                        onChange={event => {
                          dispatch(AActions.changePlanType(event.target.value as string));
                        }}
                      >
                        <MenuItem value="IND">Individual</MenuItem>
                        <MenuItem value="FAMILY">Family</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="checkedB"
                          color="secondary"
                          id="accident-replacement"
                          disabled={!replacementEnabled}
                          checked={replacement}
                          onChange={() => dispatch(AActions.changeReplacement(!replacement))}
                        />
                      }
                      label={<Typography className="case-details-section__checkbox-label">Replacement</Typography>}
                      className="product-riders-section__policy-type__form__row--field"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      id="accident-policy-number"
                      label="Policy Number"
                      variant="outlined"
                      placeholder={replacementEnabled ? 'Enter Policy Number' : ''}
                      margin="normal"
                      fullWidth
                      error={showPolicyNumberError}
                      helperText={showPolicyNumberError ? ErrorMessages.INVALID_POLICY_NUMBER : ''}
                      className="product-riders-section__policy-type__form__row--field"
                      value={policyNumber.key !== 'ValidPolicyNumber' || !replacement ? '' : policyNumber.value.value}
                      onBlur={() => setPolicyNumberBlurred(true)}
                      onChange={event => dispatch(AActions.changePolicyNumber(event.target.value as string))}
                      disabled={!replacement}
                      inputProps={{
                        maxLength: 10,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="checkedB"
                          color="secondary"
                          id="accident-override-bill-form"
                          checked={overrideBillForm === null ? false : overrideBillForm}
                          onChange={() => dispatch(AActions.changeOverrideBillForm(!overrideBillForm))}
                        />
                      }
                      label={<Typography>Override Bill Form To Direct</Typography>}
                      className="product-riders-section__policy-type__form__row--field"
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="checkedB"
                          color="secondary"
                          id="accident-section125"
                          checked={section125}
                          onChange={() => dispatch(AActions.changeSection125(!section125))}
                        />
                      }
                      label={<Typography>Section 125</Typography>}
                      className="product-riders-section__policy-type__form__row--field"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="checkedB"
                          color="secondary"
                          id="accident-select-cgi"
                          checked={selectCgi === null ? false : selectCgi}
                          onChange={() => dispatch(AActions.changeSelectCgi(!selectCgi, productType.value))}
                          disabled={!selectCgiEnabled}
                        />
                      }
                      label={<Typography>Select CGI</Typography>}
                      className="product-riders-section__policy-type__form__row--field"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="checkedB"
                          color="secondary"
                          id="accident-apdir1"
                          checked={apdir1 === null || apdir1 === false ? false : enableAPDIRCheckbox || apdir1}
                          onChange={() => dispatch(AActions.changeApdir1(!apdir1))}
                          disabled={!apdir1Enabled}
                        />
                      }
                      label={<Typography>APDIR1</Typography>}
                      className="product-riders-section__policy-type__form__row--field"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      id="accident-premium"
                      label="Premium"
                      variant="outlined"
                      placeholder="Enter Premium"
                      margin="normal"
                      fullWidth
                      className="product-riders-section__policy-type__form__row--field"
                      value={calculatedPremium.value}
                      required={section125}
                      onChange={event => {
                        dispatch(AActions.changePremium(event.target.value as string));
                        dispatch(AActions.changeCalculatedPremium(event.target.value as string));
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      id="accident-cwa"
                      label="CWA"
                      variant="outlined"
                      placeholder="Enter CWA"
                      margin="normal"
                      fullWidth
                      className="product-riders-section__policy-type__form__row--field"
                      value={cwa.value}
                      onChange={event => dispatch(AActions.changeCWA(event.target.value as string))}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      id="accident-monthly-salary"
                      label="Monthly Salary"
                      variant="outlined"
                      placeholder="Enter Monthly Salary"
                      margin="normal"
                      fullWidth
                      className="product-riders-section__policy-type__form__row--field"
                      value={monthlySalary.value}
                      onChange={event => dispatch(AActions.changeMonthlySalary(event.target.value as string))}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl
                      error={showTypeError}
                      variant="outlined"
                      className="product-riders-section__policy-type__form__row--field"
                    >
                      <InputLabel ref={setTypeLabel} id="accident-type-label">
                        Base Units
                      </InputLabel>
                      <Select
                        labelId="accident-label-type"
                        labelWidth={typeLabel?.clientWidth}
                        id="accident-type"
                        value={baseUnits.key === 'Empty' ? '' : baseUnits.value}
                        onBlur={() => setBlurType(true)}
                        onChange={event => dispatch(AActions.changeBaseUnits(event.target.value as string))}
                        displayEmpty
                      >
                        {A.baseUnits.map(baseUnit => (
                          <MenuItem value={baseUnit} key={baseUnit}>
                            {baseUnit}
                          </MenuItem>
                        ))}
                      </Select>
                      {showTypeError && <FormHelperText>{ErrorMessages.REQUIRED_MESSAGE}</FormHelperText>}
                    </FormControl>
                  </Grid>
                </Grid>

                <div>
                  <FixedCodeRiderInformation
                    riderInformation={riderInformation}
                    changeRiderInformationAction={AActions.changeRiderInformation}
                    isDisabled={(code: string) => {
                      switch (productType.value) {
                        case 'AP2':
                        case 'AP3':
                        case 'AP2C':
                        case 'AP3C':
                        case 'SAP2':
                        case 'SAP3':
                          switch (code) {
                            case 'DF':
                            case 'AUC':
                            case 'ERS':
                            case 'ADD':
                              return true;
                            default:
                              return false;
                          }
                        default:
                          return true;
                      }
                    }}
                    identifier={'ordinary-accident'}
                  />
                </div>
              </>
            ) : null}
          </div>
        </form>
      </Grid>
    </>
  );
};

export default Accident;
